"use client";
import { useTranslations } from "next-intl";
import React, { useEffect } from "react";
import * as AlertDialog from "@/components/ui/alert-dialog";
import Link from "@/lib/locale/Link";

type UploadStatus = "INITIAL" | "LOADING" | "ERROR" | "SUCCESS";

export type UploadDialogProps = {
  uploadAlertOpen: boolean;
  setUploadAlertOpen: (open: boolean) => void;
  jobId: string;
  uploadStatus: UploadStatus;
  uploadMessage: string;
  resetForm: () => void;
};

type UploadStatusDialogProps = UploadDialogProps & {
  successHref: string;
};

export const UploadStatusDialog = ({
  uploadAlertOpen,
  setUploadAlertOpen,
  uploadStatus,
  uploadMessage,
  resetForm,
  successHref,
}: UploadStatusDialogProps) => {
  const t = useTranslations();

  useEffect(() => {
    if (uploadStatus !== "INITIAL") {
      setUploadAlertOpen(true);
    }
  }, [setUploadAlertOpen, uploadStatus]);

  const renderContent = () => {
    switch (uploadStatus) {
      case "INITIAL":
        return null;
      case "SUCCESS":
        return (
          <AlertDialog.AlertDialogContent>
            <AlertDialog.AlertDialogHeader>
              <AlertDialog.AlertDialogTitle>
                {t("uploadForm.transcriptionHasBegunMessage")}
              </AlertDialog.AlertDialogTitle>
              <AlertDialog.AlertDialogDescription>
                <div>{t("uploadForm.transcriptionDialogLongMessage")}</div>
              </AlertDialog.AlertDialogDescription>
            </AlertDialog.AlertDialogHeader>
            <AlertDialog.AlertDialogFooter>
              <AlertDialog.AlertDialogCancel>
                {t("uploadForm.transcriptionDialogConfirmButton")}
              </AlertDialog.AlertDialogCancel>
              <AlertDialog.AlertDialogAction asChild>
                <Link href={successHref}>{t("uploadForm.transcriptionDialogSeeProgressLink")}</Link>
              </AlertDialog.AlertDialogAction>
            </AlertDialog.AlertDialogFooter>
          </AlertDialog.AlertDialogContent>
        );
      case "LOADING":
        return (
          <AlertDialog.AlertDialogContent>
            <AlertDialog.AlertDialogHeader>
              <AlertDialog.AlertDialogTitle>
                {t("uploadForm.isUploadingHeader")}
              </AlertDialog.AlertDialogTitle>
              <AlertDialog.AlertDialogDescription>
                <div>{uploadMessage}</div>
              </AlertDialog.AlertDialogDescription>
            </AlertDialog.AlertDialogHeader>
            <AlertDialog.AlertDialogFooter>
              <AlertDialog.AlertDialogCancel>
                {t("uploadForm.transcriptionDialogConfirmButton")}
              </AlertDialog.AlertDialogCancel>
            </AlertDialog.AlertDialogFooter>
          </AlertDialog.AlertDialogContent>
        );
      case "ERROR":
        return (
          <AlertDialog.AlertDialogContent>
            <AlertDialog.AlertDialogHeader>
              <AlertDialog.AlertDialogTitle>
                {t("uploadForm.errorUploadingMessage")}
              </AlertDialog.AlertDialogTitle>
              <AlertDialog.AlertDialogDescription>
                <div>{uploadMessage}</div>
              </AlertDialog.AlertDialogDescription>
            </AlertDialog.AlertDialogHeader>
            <AlertDialog.AlertDialogFooter>
              <AlertDialog.AlertDialogCancel>
                {t("uploadForm.transcriptionDialogConfirmButton")}
              </AlertDialog.AlertDialogCancel>
            </AlertDialog.AlertDialogFooter>
          </AlertDialog.AlertDialogContent>
        );
      default: {
        const invalidStatus: never = uploadStatus;
        console.warn("Invalid upload status", invalidStatus);
        return null;
      }
    }
  };

  return (
    <AlertDialog.AlertDialog
      open={uploadAlertOpen}
      onOpenChange={(newOpen) => {
        if (uploadStatus === "SUCCESS") {
          resetForm();
        }
        setUploadAlertOpen(newOpen);
      }}
    >
      {renderContent()}
    </AlertDialog.AlertDialog>
  );
};

export const UploadDialog = (props: UploadDialogProps) => (
  <UploadStatusDialog {...props} successHref={`/app/transcriptions/${props.jobId}`} />
);
